import {inject, Injectable} from '@angular/core';
import { UgmWeatherStationService} from '@6grain/masofa-monolith-api-client/weather';
import {Observable, of} from 'rxjs';
import { tap} from 'rxjs/operators';
import {OpenStreetMapComponent} from '@components/openstreetmap/openstreetmap.component';

@Injectable({
  providedIn: 'root'
})
export class WeatherStationManager {
  private UgmWeatherStation = inject(UgmWeatherStationService);
  private mapInstance!: OpenStreetMapComponent;
  private weatherStationMarkers: string[] = [];

  // Getter for the number of rendered weather station markers
  public get weatherStationMarkersCount(): number {
    return this.weatherStationMarkers.length;
  }

  initializeRegions(mapInstance: OpenStreetMapComponent): void {

    this.mapInstance = mapInstance;
  }


  getUgmWeatherStations(): Observable<any> {
    if (!this.mapInstance) {
      return of();
    }

    this.mapInstance.isLoading = true;

    return this.UgmWeatherStation.weatherUgmWeatherStationGetByQueryPost({}).pipe(
      tap(result => {
        this.mapInstance.isLoading = false;

        // Clear existing weather station markers BEFORE preparing and adding new ones
        this.clearWeatherStationMarkers();

        if (!this.mapInstance) {
          return;
        }

        if (result && Array.isArray(result)) {
          // Add markers one by one so we append UGM stations without replacing
          // existing markers managed by monitoring/seasons layers.
          result.forEach((station, index) => {
            if (station.latitude && station.longitude) {
              const marker = this.prepareWeatherStationMarker(station);
              if (marker) {
                // store marker id
                this.weatherStationMarkers.push(marker.id);
                try {
                  this.mapInstance.mapController$.next({ addMarker: marker });
                } catch (e) {
                  console.warn('Failed to add weather station marker via mapController', e);
                }
              }
            } else {
              console.warn(`  ✗ Station ${index + 1} missing coordinates:`, station);
            }
          });

        } else {
          console.warn('⚠️ No valid markers to add');
        }
      })
    );
  }

  private prepareWeatherStationMarker(station: any): any {
    const iconHtml = `
      <div style="
        background: white;
        border-radius: 16px;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      ">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.2742 11.9007C14.114 11.9007 15.6263 10.4825 15.7828 8.68227C16.6008 8.08434 17.0988 7.12608 17.0988 6.10014C17.0988 4.33594 15.6633 2.90045 13.899 2.90045C13.6312 2.90045 13.3699 2.93677 13.1156 3.00122C12.3117 1.76992 10.9259 0.951689 9.34917 0.951689C8.25141 0.951689 7.21722 1.34162 6.39779 2.05731C6.08988 1.9706 5.772 1.92665 5.44915 1.92665C3.69485 1.92665 2.23585 3.21508 1.96837 4.89549C0.288189 5.16267 -1 6.6216 -1 8.37584C-1 10.3193 0.581159 11.9007 2.52443 11.9007H9.39956V14.5994H7.39916C7.17181 14.5994 6.9641 14.7278 6.86244 14.9311L4.26321 20.1317C4.17034 20.318 4.1803 20.5389 4.28957 20.7152C4.39885 20.8922 4.59192 21 4.79993 21H15.1992C15.2039 21 15.208 21 15.2109 21C15.5426 21 15.8109 20.7316 15.8109 20.4C15.8109 20.2629 15.7652 20.1364 15.6879 20.0356L13.1367 14.9311C13.0353 14.7278 12.8273 14.5994 12.6 14.5994H10.5996V11.9007H12.2742ZM12.2291 15.7994L14.2283 19.8H5.77054L7.77006 15.7994H9.39956V17.7997C9.39956 18.1314 9.66821 18.3997 9.99956 18.3997C10.3309 18.3997 10.5996 18.1314 10.5996 17.7997V15.7994H12.2291ZM13.899 4.1004C15.0017 4.1004 15.8988 4.99744 15.8988 6.10014C15.8988 6.4886 15.7816 6.86036 15.5783 7.17764C15.2719 6.33773 14.6595 5.62906 13.8422 5.22243C13.8228 4.84247 13.7519 4.47656 13.6435 4.12677C13.7285 4.11593 13.8123 4.1004 13.899 4.1004ZM2.52443 10.7008C1.24269 10.7008 0.200005 9.65783 0.200005 8.37584C0.200005 7.09415 1.24269 6.05151 2.52443 6.05151C2.85578 6.05151 3.12443 5.78286 3.12443 5.45153C3.12443 4.16954 4.16741 3.12661 5.44915 3.12661C5.74915 3.12661 6.04241 3.18432 6.32074 3.2977C6.5472 3.3891 6.80736 3.33549 6.97875 3.15942C7.60746 2.50964 8.44946 2.15165 9.34917 2.15165C11.1679 2.15165 12.648 3.63196 12.648 5.45153L12.641 5.59947C12.6322 5.86138 12.7951 6.09809 13.0418 6.1848C13.9711 6.51057 14.5957 7.38857 14.5974 8.37086C14.598 8.38199 14.5945 8.39254 14.5951 8.40338C14.5799 9.67247 13.5463 10.7008 12.2742 10.7008H10.5996V9.19407C11.3525 8.94125 11.8998 8.23698 11.8998 7.40029C11.8998 6.35267 11.0472 5.50016 9.99956 5.50016C8.9519 5.50016 8.09936 6.35267 8.09936 7.40029C8.09936 8.23698 8.64692 8.94125 9.39956 9.19407V10.7008H2.52443ZM9.99956 8.10046C9.61343 8.10046 9.29936 7.78641 9.29936 7.40029C9.29936 7.01417 9.61343 6.70012 9.99956 6.70012C10.3857 6.70012 10.6998 7.01417 10.6998 7.40029C10.6998 7.78641 10.3857 8.10046 9.99956 8.10046ZM21 6.10014C21 6.43147 20.7316 6.70012 20.4 6.70012H18.9996C18.668 6.70012 18.3996 6.43147 18.3996 6.10014C18.3996 5.7688 18.668 5.50016 18.9996 5.50016H20.4C20.7316 5.50016 21 5.7688 21 6.10014ZM17.0812 2.91802C16.8469 2.68366 16.8469 2.30398 17.0812 2.06962L18.0715 1.07942C18.3058 0.845052 18.6855 0.845052 18.9199 1.07942C19.1543 1.31379 19.1543 1.69346 18.9199 1.92783L17.9297 2.91802C17.8125 3.03521 17.659 3.0938 17.5055 3.0938C17.3519 3.0938 17.1984 3.03521 17.0812 2.91802ZM13.299 1.00032V-0.400021C13.299 -0.731357 13.5673 -1 13.899 -1C14.2306 -1 14.499 -0.731357 14.499 -0.400021V1.00032C14.499 1.33166 14.2306 1.6003 13.899 1.6003C13.5673 1.6003 13.299 1.33166 13.299 1.00032ZM18.9199 10.273C19.1543 10.5074 19.1543 10.8871 18.9199 11.1214C18.8027 11.2386 18.6492 11.2972 18.4957 11.2972C18.3422 11.2972 18.1887 11.2386 18.0715 11.1214L17.0812 10.1312C16.8469 9.89688 16.8469 9.5172 17.0812 9.28284C17.3156 9.04847 17.6953 9.04847 17.9297 9.28284L18.9199 10.273Z" fill="#0F1324" fill-opacity="0.6"/>
        </svg>
      </div>
    `;

    // Use a stable unique identifier: prefer station.id, then ugmRegionId, then coordinates
    const uniqueId = station.id || station.ugmRegionId || `${station.latitude}-${station.longitude}`;
    const markerId = `weather_station_${uniqueId}`;

    return {
      id: markerId,
      lat: station.latitude,
      lng: station.longitude,
      customIcon: iconHtml,
      title: station.name || 'Weather Station',
      popup: station.name || 'Weather Station',
      withCluster: false, // Disable clustering for UGM weather stations
      onClick: (data: any) => {
        console.log('Weather station clicked:', data);
      }
    };
  }

  clearWeatherStationMarkers(): void {

    if (this.weatherStationMarkers.length === 0) {
      return;
    }

    // Remove each weather station marker individually by ID
    this.weatherStationMarkers.forEach((markerId, index) => {
      try {
        this.mapInstance?.mapController$.next({ removeMarker: { id: markerId } });
      } catch (e) {
        console.warn('Failed to remove weather station marker', markerId, e);
      }
    });

    // Reset the markers array
    this.weatherStationMarkers = [];
  }

  /**
   * Return a shallow copy of current weather station marker IDs
   * Use-case: callers can explicitly request mapController to remove markers
   */
  public getWeatherStationMarkerIds(): string[] {
    return [...this.weatherStationMarkers];
  }

}
